import React from 'react';
import { Loader2 } from 'lucide-react';

type Variant = 'primary' | 'secondary' | 'ghost' | 'destructive';
type Size = 'sm' | 'md';

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  loading?: boolean;
  /** Icon rendered before the label. */
  icon?: React.ReactNode;
}

/**
 * Desktop action vocabulary. One shape for every button on the desktop
 * surfaces so primary, secondary and destructive read correctly at a glance.
 *
 * - primary: the one commit action on a screen (Spara, Nytt projekt).
 * - secondary: bordered, for frequent non-committing actions.
 * - ghost: text-weight, for tertiary and inline actions.
 * - destructive: red text on hover fill; never adjacent to primary.
 *
 * Text colours sit at AA on both cream and white. All sizes clear the 32px
 * desktop minimum; `md` is 36px.
 */
const VARIANT: Record<Variant, string> = {
  primary:
    'bg-accent-600 text-white hover:bg-accent-700 active:bg-accent-800 disabled:bg-accent-600/50 shadow-ios-control',
  secondary:
    'bg-[var(--bg-surface)] text-[var(--text-primary)] border border-[var(--border-default)] hover:bg-[var(--bg-surface-hover)] active:bg-[var(--bg-surface-alt)] disabled:opacity-50',
  ghost:
    'text-[var(--text-secondary)] hover:bg-[var(--bg-surface-alt)] hover:text-[var(--text-primary)] active:bg-[var(--bg-surface-hover)] disabled:opacity-50',
  destructive:
    'text-red-700 dark:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/20 active:bg-red-100 dark:active:bg-red-900/30 disabled:opacity-50',
};

const SIZE: Record<Size, string> = {
  sm: 'h-[32px] px-3 text-[13px] gap-1.5',
  md: 'h-[36px] px-3.5 text-sm gap-2',
};

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'secondary', size = 'md', loading = false, icon, className = '', children, disabled, type = 'button', ...rest },
  ref
) {
  return (
    <button
      ref={ref}
      type={type}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      className={`inline-flex items-center justify-center whitespace-nowrap rounded-ios-control font-medium select-none transition-colors duration-150 disabled:cursor-not-allowed ${VARIANT[variant]} ${SIZE[size]} ${className}`}
      {...rest}
    >
      {loading ? <Loader2 size={size === 'sm' ? 14 : 16} className="animate-spin" aria-hidden="true" /> : icon}
      {children}
    </button>
  );
});

export default Button;
